import React, { useState, useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { getOfficeArchivedFees, restoreOfficeFee } from "../../redux/slices/FeeSlice";
import { Fee } from "../../models/fee.model";
import { TableHeader, TableRow, Pagination, Button } from "../../components";
import { headersArchivedFee } from "./FeesData";
import { formatNumToDec, getSorting } from "../../utils";
import { CircularProgress } from "@mui/material";
import DataNotFound from "../../components/DataNotFound/DataNotFound";
import NotFound from "../../assets/images/no-fees.png";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import { ToastVariants } from "../../models";
import { sortObj } from "../../redux/slices/OfficeSlice";

interface ArchivedFeesProps {
  officeId: string;
}

const ArchivedFees: React.FC<ArchivedFeesProps> = ({ officeId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useContext(ToastContext);
  const { archivedFees, loading } = useSelector((state: RootState) => state.fees);
  const [sort, setSort] = useState<sortObj>({
    pageNumber: 0,
    rowsPerPage: 10,
    sortedBy: "name,asc",
  });
  const [sortMethods, setSortMethods] = useState([true, false, false, false]);
  const [activeColumn, setActiveColumn] = useState<number>(-1);
  const [restoringFeeId, setRestoringFeeId] = useState<string | null>(null);

  const headers = headersArchivedFee;
  const sortKeys = headers.map(head => head.sortKey);
  const extraClasses = headers.map(head => head.extraClasses);

  const headerIconAsc = <p> ↑</p>;
  const headerIconDesc = <p> ↓</p>;
  const sortIcons = sortMethods.map((item, index) => (activeColumn === index ? (item ? headerIconAsc : headerIconDesc) : null));

  const fetchArchivedFees = async (sortObj?: sortObj) => {
    const pageNumToBeCalled = sortObj?.pageNumber ?? sort.pageNumber;
    const sizeToBeCalled = sortObj?.rowsPerPage ?? sort.rowsPerPage;
    const sortBy = sortObj?.sortedBy ?? sort.sortedBy;

    await dispatch(
      getOfficeArchivedFees({
        officeId,
        page: pageNumToBeCalled,
        size: sizeToBeCalled,
        sort: sortBy,
      })
    );
  };

  useEffect(() => {
    if (officeId) {
      fetchArchivedFees();
    }
  }, [officeId]);

  const onPageChange = async (e: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    const newSort = { ...sort, pageNumber: newPage };
    setSort(newSort);
    await fetchArchivedFees(newSort);
  };

  const onRowsPerPageChange = async (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newSort = { ...sort, pageNumber: 0, rowsPerPage: parseInt(event.target.value, 10) };
    setSort(newSort);
    await fetchArchivedFees(newSort);
  };

  const onHeaderClick = (index: number) => {
    if (sortKeys[index] === "" || loading) return;
    const sortMethodString = getSorting(sortKeys, index, sortMethods);
    setActiveColumn(index);
    setSortMethods([...sortMethods.slice(0, index), !sortMethods[index], ...sortMethods.slice(index + 1)]);
    const newSort = { ...sort, sortedBy: sortMethodString, pageNumber: 0 };
    setSort(newSort);
    fetchArchivedFees(newSort);
  };

  const handleRestore = async (fee: Fee) => {
    if (!fee?.feeId || !officeId) return;

    try {
      setRestoringFeeId(fee.feeId);
      const result = await dispatch(restoreOfficeFee({ officeId, fee }));
      if (result.payload) {
        toast?.openToast("Fee restored successfully", 2000, ToastVariants.Success);
        // Refresh the archived fees list
        await fetchArchivedFees(sort);
      } else {
        toast?.openToast("Failed to restore fee", 2000, ToastVariants.Error);
      }
    } catch (error) {
      toast?.openToast("Failed to restore fee", 2000, ToastVariants.Error);
    } finally {
      setRestoringFeeId(null);
    }
  };

  if (loading && !archivedFees) {
    return <CircularProgress size={40} style={{ marginLeft: "50%", marginTop: "10%" }} />;
  }

  return (
    <div className="block">
      {archivedFees?.content && archivedFees.content.length > 0 ? (
        <>
          <div className="flex justify-between items-center border-b border-docnote-blue-light border-opacity-50">
            {headers.map((head, index: number) => (
              <TableHeader
                title={head.title}
                index={index}
                key={index}
                sortIcon={sortIcons[index]}
                isClickable={head.isClickable}
                handleSort={onHeaderClick}
                extraClassesHeader={extraClasses[index]}
              />
            ))}
          </div>

          {archivedFees.content.map((rowData: Fee, index: number) => {
            const sortedData = {
              name: rowData.name,
              price: "$" + formatNumToDec(rowData.price / 100, 2),
              description: rowData.description || "",
              action: (
                <div onClick={e => e.stopPropagation()}>
                  <Button
                    text="Restore"
                    onClick={() => handleRestore(rowData)}
                    disabled={restoringFeeId === rowData.feeId || loading}
                    className="whitespace-nowrap w-auto"
                  />
                </div>
              ),
            };

            return (
              <TableRow
                data={Object.values(sortedData)}
                key={index}
                id={rowData.feeId}
                extraClassesRow={extraClasses}
                tooltipVisible={false}
                handleOpenTooltip={() => {}}
                handleCloseTooltip={() => {}}
              />
            );
          })}

          <Pagination
            colSpan={4}
            count={archivedFees.totalElements || 0}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            page={sort.pageNumber}
            rowsPerPage={sort.rowsPerPage}
          />
        </>
      ) : (
        <DataNotFound image={NotFound} text="No archived fees found" />
      )}
    </div>
  );
};

export default ArchivedFees;
